import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { AndExpression } from "../../Expressions/AndExpression";
import { ComparisonExpression } from "../../Expressions/ComparisonExpression";
import { PartialNullable } from "../../Extensions";
import { JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { Dialog, DialogPropertyValues, IDialog } from "../Controls/Dialog";
import { EntitySearch } from "../Controls/EntitySearch";
import { EntitySearchSortDirectionEnum } from "../Controls/EntitySearchSortDirectionEnum";
import { Panel } from "../Controls/Panel";
import { PropertyEditorDateTime } from "../Controls/PropertyEditorDateTime";
import { PropertyEditorText } from "../Controls/PropertyEditorText";
import { NAMESPACE } from "./_Namespace";

export type EntityAuditDialogPropertyValues =
    DialogPropertyValues
    & {
        applicationEntitySupportsSoftDelete: boolean;
        entitySetName: string;
        recordId: string;
    };

export type EntityAuditDialogProperties = {
    [name in keyof EntityAuditDialogPropertyValues]: Property<EntityAuditDialogPropertyValues[name]>
};

export interface IEntityAuditDialog extends IDialog, EntityAuditDialogProperties
{
}

export class EntityAuditDialog extends Dialog implements IEntityAuditDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntityAuditDialog";
    public static override className = "EntityAuditDialog";

    // #region Global Variables

    private static instance: EntityAuditDialog;

    // #endregion

    // #region Public Methods

    public static getSingleInstance()
    {
        if (!this.instance)
            this.instance = new EntityAuditDialog();

        return this.instance;
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntityAuditDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                title: "Audit Information",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$noResults?: JNative<HTMLElement>;
    protected entitySearch?: EntitySearch;
    protected propertyEditorCreatedBy?: PropertyEditorText;
    protected propertyEditorCreatedDate?: PropertyEditorText;
    protected propertyEditorLastUpdatedBy?: PropertyEditorText;
    protected propertyEditorLastUpdatedDate?: PropertyEditorText;
    protected propertyEditorDeletedBy?: PropertyEditorText;
    protected propertyEditorDeletedDate?: PropertyEditorText;

    // #endregion

    // #region Properties

    public applicationEntitySupportsSoftDelete!: PropertyBoolean;
    public entitySetName!: PropertyString;
    public recordId!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntityAuditDialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-dialog-entity-audit");
    }

    // #endregion

    // #region Protected Methods

    protected _applicationEntitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _recordIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: EntityAuditDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.applicationEntitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.applicationEntitySupportsSoftDelete, this._applicationEntitySupportsSoftDeleteChanged);
        this.entitySetName = new PropertyString(this, propertyValues.entitySetName, this._entitySetNameChanged);
        this.recordId = new PropertyString(this, propertyValues.recordId, this._recordIdChanged);
    }

    protected override _activateAsync()
    {
        const
            entitySearch = this.entitySearch,
            $$noResults = this.$$noResults,
            promises = [],
            entitySetName = this.entitySetName.get(),
            recordId = this.recordId.get(),
            properties = [
                "createdByUser.Fullname",
                "createdDate",
                "lastUpdatedByUser.Fullname",
                "lastUpdatedDate"
            ];

        Assert.isNotNullOrUndefined($$noResults, "$$noResults");
        Assert.isNotNullOrUndefined(entitySearch, "entitySearch");
        Assert.isNotNullOrUndefined(entitySetName, "entitySetName");
        Assert.isNotNullOrUndefined(recordId, "recordId");

        if (this.applicationEntitySupportsSoftDelete.get())
        {
            properties.push(...[
                "deletedByUser.Fullname",
                "deletedDate",
                "deletedGuid"
            ]);
        }

        promises.push(
            ServiceManager.getRequired(WebApiClientService)
                .createEntityGetRequest(
                    entitySetName,
                    recordId,
                    properties,
                    { "RAD-Option-Include-Soft-Deleted": true }
                )
                .executeAsync()
        );

        promises.push(
            entitySearch
                .searchAsync(0, new AndExpression({
                    expressions: [
                        new ComparisonExpression({
                            path: "entityProperty.entity.namePlural",
                            value: entitySetName ? entitySetName : null
                        }),
                        new ComparisonExpression({
                            path: "recordId",
                            value: recordId ? recordId : null
                        })
                    ]
                }), true)
                .then(() =>
                {
                    if (entitySearch.searchResultsCount.get())
                    {
                        entitySearch.show();
                        $$noResults.hide();
                    }
                    else
                    {
                        entitySearch.hide();
                        $$noResults.show();
                    }
                })
        );

        return (
            Promise.all(promises)
                .then(results =>
                {
                    this.dataSource.set(new DataSource({ data: results[0] }));
                })
        );
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        let
            panel;

        panel = new Panel().appendTo(this);
        panel.append(JNativeStatic.create$$("h4", null, "Record Information"));

        panel = new Panel().appendTo(this);
        this.propertyEditorCreatedBy = new PropertyEditorText({ propertyDisplayName: "Created By", dataSourceProperty: "createdByUser.fullName" }).appendTo(panel);
        this.propertyEditorCreatedDate = new PropertyEditorDateTime({ propertyDisplayName: "Created Date", dataSourceProperty: "createdDate" }).appendTo(panel);
        this.propertyEditorLastUpdatedBy = new PropertyEditorText({ propertyDisplayName: "Last Updated By", dataSourceProperty: "lastUpdatedByUser.fullName" }).appendTo(panel);
        this.propertyEditorLastUpdatedDate = new PropertyEditorDateTime({ propertyDisplayName: "Last Updated Date", dataSourceProperty: "lastUpdatedDate" }).appendTo(panel);
        this.propertyEditorDeletedBy = new PropertyEditorText({ propertyDisplayName: "Deleted By", dataSourceProperty: "deletedByUser.fullName" }).appendTo(panel);
        this.propertyEditorDeletedDate = new PropertyEditorDateTime({ propertyDisplayName: "Deleted Date", dataSourceProperty: "deletedDate" }).appendTo(panel);

        panel = new Panel().appendTo(this);
        panel.append(JNativeStatic.create$$("h4", null, "Property Change Information"));

        this.$$noResults = JNativeStatic.create$$("p", null, "No property change information available").hide();
        panel.append(this.$$noResults);

        this.entitySearch =
            new EntitySearch({
                applicationEntityName: "EntityPropertyAuditLogItem",
                applicationEntityNameFriendly: "Entity Property Audit Log Item",
                applicationEntityNamePlural: "EntityPropertyAuditLogItems",
                applicationEntityNamePluralFriendly: "Entity Property Audit Log Items",
                defaultSortListProperty: "createdDate",
                defaultSortListPropertyDirection: EntitySearchSortDirectionEnum.descending,
                listProperties: [
                    { path: "createdDate", name: "Date" },
                    { path: "entityProperty.displayName", name: "Property", isString: true },
                    { path: "oldDescription", name: "From", isString: true },
                    { path: "newDescription", name: "To", isString: true },
                    { path: "createdByUser.fullName", name: "User", isString: true }
                ],
                showEntityCommands: false
            })
                .appendTo(this);

        Assert.isNotNullOrUndefined(this.entitySearch.tableSearchResults, "this.entitySearch.tableSearchResults");

        this.entitySearch.tableSearchResults.rowClickable.set(false);
        this.entitySearch.tableSearchResults.rowIcon.set(null);
        this.entitySearch.tableSearchResults.rowIconMobile.set(null);

        return $$element;
    }
    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);

        this.propertyEditorDeletedBy?.visible.set(!!(change.currentValue?.propertyValueByPathGet("deletedGuid")));
        this.propertyEditorDeletedDate?.visible.set(!!(change.currentValue?.propertyValueByPathGet("deletedGuid")));
    }

    public override openAsync(data: { entitySetName: string, recordId: string, applicationEntitySupportsSoftDelete?: boolean })
    {
        this.entitySetName.set(data.entitySetName);
        this.recordId.set(data.recordId);
        this.applicationEntitySupportsSoftDelete.set(!!data.applicationEntitySupportsSoftDelete);
        return super.openAsync();
    }

    // #endregion

    // #endregion
}